import React, { useEffect, useRef } from "react";

// Fundo animado "warp speed": raios radiais que saem do centro da tela,
// como o efeito atrás do card de login no mockup fusao-grid-particulas.html.
// Cores adaptadas pra tons de azul do tema real (var(--accent)/var(--accent2)
// em styles/global.css) em vez do azul genérico do mockup original.
//
// Uso: <WarpBackground /> dentro de um container position:relative (ex: a
// tela de login), como primeiro filho. Fica fixed + pointer-events:none.
export default function WarpBackground() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    let W, H, cxPt, cyPt, rays = [];
    let raf = null;

    const RAY_COUNT = 170;
    const BLUES = ["#1e3a8a", "#1d4ed8", "#2563eb", "#3b82f6", "#60a5fa", "#93c5fd"];

    function resize() {
      W = canvas.width = window.innerWidth;
      H = canvas.height = window.innerHeight;
      cxPt = W / 2; cyPt = H / 2;
    }
    function maxR() { return Math.hypot(W, H) / 2 + 60; }

    function newRay(spawnAtEdgeRandom) {
      const isHero = Math.random() < 0.16;
      return {
        angle: Math.random() * Math.PI * 2,
        r: spawnAtEdgeRandom ? Math.random() * maxR() : Math.random() * 40,
        speed: 0.12 + Math.random() * 0.22,
        color: isHero ? "#dbeafe" : BLUES[Math.floor(Math.random() * BLUES.length)],
        hero: isHero,
        baseWidth: isHero ? 2.2 : 0.9 + Math.random() * 0.8,
      };
    }

    function initRays() {
      rays = [];
      for (let i = 0; i < RAY_COUNT; i++) rays.push(newRay(true));
    }

    function drawStatic() {
      ctx.clearRect(0, 0, W, H);
      rays.forEach((ray) => {
        const trail = 70;
        const x1 = cxPt + Math.cos(ray.angle) * (ray.r - trail);
        const y1 = cyPt + Math.sin(ray.angle) * (ray.r - trail);
        const x2 = cxPt + Math.cos(ray.angle) * ray.r;
        const y2 = cyPt + Math.sin(ray.angle) * ray.r;
        ctx.strokeStyle = ray.color;
        ctx.globalAlpha = 0.5;
        ctx.lineWidth = ray.baseWidth;
        ctx.beginPath(); ctx.moveTo(x1, y1); ctx.lineTo(x2, y2); ctx.stroke();
      });
      ctx.globalAlpha = 1;
    }

    function frame() {
      if (!document.hidden) {
        ctx.globalCompositeOperation = "destination-out";
        ctx.fillStyle = "rgba(0,0,0,0.16)";
        ctx.fillRect(0, 0, W, H);
        ctx.globalCompositeOperation = "lighter";
        const mr = maxR();
        rays.forEach((ray) => {
          ray.r += ray.speed;
          ray.speed *= 1.003;
          if (ray.r > mr) { Object.assign(ray, newRay(false)); return; }
          const progress = ray.r / mr;
          const trailLen = Math.min(ray.speed * 7, 200);
          const x1 = cxPt + Math.cos(ray.angle) * Math.max(0, ray.r - trailLen);
          const y1 = cyPt + Math.sin(ray.angle) * Math.max(0, ray.r - trailLen);
          const x2 = cxPt + Math.cos(ray.angle) * ray.r;
          const y2 = cyPt + Math.sin(ray.angle) * ray.r;
          const alpha = Math.min(progress * 1.6, 1) * (ray.hero ? 0.85 : 0.5);

          ctx.strokeStyle = ray.color;
          ctx.globalAlpha = alpha;
          ctx.lineWidth = ray.baseWidth * (0.5 + progress * 1.3);
          ctx.beginPath(); ctx.moveTo(x1, y1); ctx.lineTo(x2, y2); ctx.stroke();

          if (ray.hero) {
            ctx.globalAlpha = alpha;
            ctx.fillStyle = "#ffffff";
            ctx.beginPath(); ctx.arc(x2, y2, 1.6, 0, Math.PI * 2); ctx.fill();
          }
        });
        ctx.globalCompositeOperation = "source-over";
        ctx.globalAlpha = 1;
      }
      if (!reduced) raf = requestAnimationFrame(frame);
    }

    resize();
    initRays();
    window.addEventListener("resize", resize);

    if (reduced) {
      drawStatic();
    } else {
      raf = requestAnimationFrame(frame);
    }

    return () => {
      if (raf) cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      style={{ position: "fixed", inset: 0, zIndex: 0, pointerEvents: "none" }}
    />
  );
}
